import {User} from '@hconnect/apiclient'
import {Button, Modal, ModalContent, ModalFooter, ModalTitle} from '@hm/fundamental'
import {Snackbar} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import {mdiContentCopy} from '@mdi/js'
import {CircularProgress} from '@mui/material'
import intlTelInput from 'intl-tel-input'
import React, {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'

import {useLoggedInUserProfile} from '../../../hooks/useLoggedInUser'
import {useTranslation} from '../../../hooks/useTranslation'
import {useTfaSetup} from '../hooks/useTfaSetup'

import {AddTfaDialogContent} from './AddTfaDialogContent/AddTfaDialogContent'
import {DeleteTfaDialogContent} from './DeleteTfaDialogContent/DeleteTfaDialogContent'
import {EditTfaDialogContent} from './EditTfaDialogContent/EditTfaDialogContent'
import {TfaDialogSkeleton} from './TfaDialogSkeleton/TfaDialogSkeleton'

type TfaDialogProps = {
  setShowTfaDialog: (show: boolean) => void
  showTfaDialog: boolean
  tfaType: 'authenticatorApp' | 'phoneNumber' | 'none'
  mobileNumber?: string
  edit?: boolean
  loggedInUserProfile?: User | null
}

export enum TfaType {
  APP = 0,
  MOBILE = 1
}

export type DeleteConfirmationType = {
  tfaType: 'authenticatorApp' | 'phoneNumber'
  reason: 'delete' | 'replace'
}

export type PhoneDataType = {
  phoneNumber?: string
  countryData?: intlTelInput.CountryData
}

const steps = ['1', '2', '3', '4']
// eslint-disable-next-line complexity
export const TfaDialog = ({
  setShowTfaDialog,
  showTfaDialog,
  tfaType,
  mobileNumber,
  edit,
  loggedInUserProfile
}: TfaDialogProps) => {
  const {t} = useTranslation()
  const [activeStep, setActiveStep] = useState(0)

  const loggedInUserProfileData = useLoggedInUserProfile(loggedInUserProfile)
  const {userId} = useParams<{userId: string}>()

  const canSetupAppTfa = userId === loggedInUserProfileData?.user_id

  const [activeTab, setActiveTab] = useState(canSetupAppTfa ? TfaType.APP : TfaType.MOBILE)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<
    DeleteConfirmationType | undefined
  >()
  const [editMode, setEditMode] = useState(edit)
  const [phoneData, setPhoneData] = useState<PhoneDataType | undefined>({phoneNumber: mobileNumber})
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false)

  useEffect(() => {
    if (mobileNumber) {
      setPhoneData({...phoneData, phoneNumber: mobileNumber})
    }
  }, [mobileNumber])

  const {
    otpData,
    refetchOtpData,
    addPhoneNumber,
    verifyMobileCode,
    verifyAppCode,
    deleteTfa,
    isVerifyMobileCodeError,
    isVerifyAppCodeError,
    isOtpUriLoading,
    isPhoneNumberLoading,
    isVerifyMobileCodeLoading,
    isVerifyAppCodeLoading,
    isDeleteTfaLoading,
    isAddPhoneNumberError,
    recoveryCodesData
  } = useTfaSetup(
    userId || '',
    activeStep,
    setActiveStep,
    activeTab,
    setActiveTab,
    setErrorMessage,
    !!editMode,
    setEditMode,
    setShowDeleteConfirmation,
    showDeleteConfirmation
  )
  const [code, setCode] = useState('')

  useEffect(() => {
    if (activeTab === TfaType.APP && activeStep === 1) {
      refetchOtpData()
    }

    if (activeTab === TfaType.MOBILE && activeStep === 1) {
      deleteTfa()
    }
  }, [activeStep])

  const handleSteps = () => {
    if (activeStep === 0) {
      setActiveStep(activeStep + 1)
    }

    if (activeStep === 1 && activeTab === TfaType.MOBILE) {
      addPhoneNumber({userIdentifier: userId || '', phoneNumber: phoneData?.phoneNumber || ''})
    }

    if (activeStep === 1 && activeTab === TfaType.APP) {
      setActiveStep(activeStep + 1)
    }

    if (activeStep === 2 && activeTab === TfaType.MOBILE) {
      verifyMobileCode({phoneNumber: phoneData?.phoneNumber || '', code})
    }

    if (activeStep === 2 && activeTab === TfaType.APP) {
      verifyAppCode({code})
    }

    if (activeStep === 3) {
      setShowTfaDialog(false)
    }
  }

  const copyText = async (text: string) => {
    await navigator.clipboard.writeText(text).then(() => setIsSnackbarOpen(true))
  }

  const isLoading = isPhoneNumberLoading || isVerifyAppCodeLoading || isVerifyMobileCodeLoading

  return (
    <Modal open={showTfaDialog} data-test-id="tfa-dialog" onClose={() => setShowTfaDialog(false)}>
      <ModalTitle>{t('userInformationForm.tfa.title')}</ModalTitle>
      <ModalContent>
        {isOtpUriLoading && <TfaDialogSkeleton />}
        {!isOtpUriLoading && (
          <>
            {editMode && !showDeleteConfirmation && (
              <EditTfaDialogContent
                tfaType={tfaType}
                setShowDeleteConfirmation={setShowDeleteConfirmation}
                setEditMode={setEditMode}
              />
            )}
            {showDeleteConfirmation && (
              <DeleteTfaDialogContent showDeleteConfirmation={showDeleteConfirmation} />
            )}
            {!editMode && (
              <AddTfaDialogContent
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                activeStep={activeStep}
                steps={steps}
                otpData={otpData}
                isVerifyAppCodeError={isVerifyAppCodeError}
                isVerifyMobileCodeError={isVerifyMobileCodeError}
                errorMessage={errorMessage}
                setCode={setCode}
                isAddPhoneNumberError={isAddPhoneNumberError}
                recoveryCodesData={recoveryCodesData}
                canSetupAppTfa={canSetupAppTfa}
                phoneData={phoneData}
                setPhoneData={setPhoneData}
              />
            )}
          </>
        )}
      </ModalContent>
      <ModalFooter>
        {activeStep === 3 && recoveryCodesData && (
          <Button
            data-test-id="copy-recovery-codes"
            variant="secondary"
            onClick={() => copyText(recoveryCodesData.toString())}
            icon={mdiContentCopy}
          >
            {t('userInformationForm.tfa.add.copyCodesToClipboard')}
          </Button>
        )}
        {activeStep + 1 !== steps.length && !editMode && !isOtpUriLoading && (
          <Button
            onClick={() =>
              activeStep + 1 < steps.length && activeStep > 0
                ? setActiveStep(activeStep - 1)
                : setShowTfaDialog(false)
            }
            variant="tertiary"
          >
            {activeStep + 1 < steps.length && activeStep > 0
              ? t('userInformationForm.tfa.back')
              : t('userInformationForm.tfa.cancel')}
          </Button>
        )}
        {!editMode && !isOtpUriLoading && (
          <Button
            onClick={() => handleSteps()}
            data-test-id={`step-button-${activeStep + 1}`}
            disabled={isLoading}
            icon={isLoading ? <CircularProgress size={16} /> : undefined}
          >
            {activeStep + 1 < steps.length
              ? t('userInformationForm.tfa.next')
              : t('userInformationForm.tfa.close')}
          </Button>
        )}
        {!showDeleteConfirmation && editMode && (
          <Button onClick={() => setShowTfaDialog(false)} variant="tertiary">
            {t('userInformationForm.tfa.cancel')}
          </Button>
        )}
        {showDeleteConfirmation && (
          <div>
            {!isDeleteTfaLoading && (
              <Button onClick={() => setShowDeleteConfirmation(undefined)} variant="tertiary">
                {t('userInformationForm.tfa.cancel')}
              </Button>
            )}
            <Button
              onClick={() => deleteTfa()}
              variant="negative"
              icon={
                isDeleteTfaLoading ? (
                  <CircularProgress size={16} className="!text-white" />
                ) : undefined
              }
            >
              {showDeleteConfirmation.reason === 'delete'
                ? t('userInformationForm.tfa.deleteConfirmation')
                : t('userInformationForm.tfa.replaceConfirmation')}
            </Button>
          </div>
        )}
      </ModalFooter>
      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setIsSnackbarOpen(false)}
      >
        <Alert onClose={() => setIsSnackbarOpen(false)} severity="success">
          {t('userInformationForm.tfa.add.copied')}
        </Alert>
      </Snackbar>
    </Modal>
  )
}
